export { getDynamicBuilder, getLookupFn } from '@polkadot-api/metadata-builders';
import { toHex } from '@polkadot-api/utils';
export { fromHex, toHex } from '@polkadot-api/utils';
import { metadata, Binary } from '@polkadot-api/substrate-bindings';
export { metadata, v14, v15 } from '@polkadot-api/substrate-bindings';
import anylogger from 'anylogger';
import { Struct, u32, u8 } from 'scale-ts';

/** Constant: https://docs.substrate.io/build/application-development/#metadata-format */
const magicNumber = 1635018093;

var packageJson = {
	name: "@talismn/scale",
	version: "0.0.0-pr1640-20241008075233",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/scale",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-scale.cjs.js",
	module: "dist/talismn-scale.esm.js",
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist .turbo node_modules"
	},
	dependencies: {
		"@polkadot-api/metadata-builders": "^0.5.0",
		"@polkadot-api/substrate-bindings": "^0.6.3",
		"@polkadot-api/utils": "^0.1.1",
		anylogger: "^1.0.11",
		"scale-ts": "^1.6.0"
	},
	devDependencies: {
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		eslint: "^8.52.0",
		jest: "^29.7",
		"ts-jest": "^29.1.1",
		typescript: "^5.2.2"
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

/**
 * Converts a `Metadata` into a `MiniMetadata`.
 *
 * A `MiniMetadata` only contains the types inside of its `lookup` which are relevant for
 * the storage queries specified in `palletsAndItems`.
 *
 * E.g. if `palletsAndItems` is `{ pallet: "System", items: ["Account"] }`, then only the
 * types used in the `System.Account` storage query will remain inside of metadata.lookups.
 */
const compactMetadata = (metadata, palletsAndItems, extraKeepTypes) => {
  // remove pallets we don't care about
  metadata.pallets = metadata.pallets.filter(pallet =>
  // keep this pallet if it's listed in `palletsAndItems`
  palletsAndItems.some(({
    pallet: palletName
  }) => pallet.name === palletName));

  // remove fields we don't care about from each pallet, and extract types for each storage item we care about
  const items = palletsAndItems.flatMap(({
    pallet: palletName,
    items: itemNames
  }) => {
    const pallet = metadata.pallets.find(pallet => pallet.name === palletName);
    if (!pallet) {
      log.debug("Failed to find pallet", palletName);
      return [];
    }

    // remove pallet fields we don't care about
    pallet.calls = undefined;
    pallet.constants = [];
    // v15 (NOT v14) has docs
    if ("docs" in pallet) pallet.docs = [];
    pallet.errors = undefined;
    pallet.events = undefined;
    if (!pallet.storage) return [];

    // filter and extract storage items we care about
    pallet.storage.items = pallet.storage.items.filter(item => itemNames.some(itemName => item.name === itemName));
    return pallet.storage.items;
  });

  // this is a set of type ids which we plan to keep in our compacted metadata
  // anything not in this set will be deleted
  // we start off with just the types of the state calls we plan to make,
  // then we run those types through a function (addDependentTypes) which will also include
  // all of the types which those types depend on - recursively
  const keepTypes = new Set(items.flatMap(item => [
  // each type can be either "Plain" or "Map"
  // if it's "Plain" we only need to get the value type
  // if it's a "Map" we want to keep both the key AND the value types
  item.type.tag === "plain" && item.type.value, item.type.tag === "map" && item.type.value.key, item.type.tag === "map" && item.type.value.value]).filter(type => typeof type === "number"));
  extraKeepTypes?.forEach(type => keepTypes.add(type));

  // recursively find all the types which our keepTypes depend on and add them to the keepTypes set
  const metadataTysMap = new Map(metadata.lookup.map(ty => [ty.id, ty]));
  addDependentTypes(metadataTysMap, keepTypes, [...keepTypes]);

  // ditch the types we aren't keeping
  metadata.lookup = metadata.lookup.filter(type => keepTypes.has(type.id));

  // update all type ids to be sequential (fill the gaps left by the deleted types)
  const newTypeIds = new Map();
  metadata.lookup.forEach((type, index) => newTypeIds.set(type.id, index));
  const getNewTypeId = oldTypeId => {
    const newTypeId = newTypeIds.get(oldTypeId);
    if (typeof newTypeId !== "number") log.error(`Failed to find newTypeId for type ${oldTypeId}`);
    return newTypeId ?? 0;
  };
  remapTypeIds(metadata, getNewTypeId);

  // ditch the remaining data we don't need to keep in a miniMetata
  if ("apis" in metadata) {
    // metadata is v15 (NOT v14)
    metadata.apis = [];
  }
  if ("address" in metadata.extrinsic) {
    // metadata is v15 (NOT v14)
    metadata.extrinsic.address = 0;
    metadata.extrinsic.call = 0;
    metadata.extrinsic.extra = 0;
    metadata.extrinsic.signature = 0;
  }
  metadata.extrinsic.signedExtensions = [];
  if ("outerEnums" in metadata) {
    // metadata is v15 (NOT v14)
    metadata.outerEnums.call = 0;
    metadata.outerEnums.error = 0;
    metadata.outerEnums.event = 0;
  }
};
const addDependentTypes = (metadataTysMap, keepTypes, types,
// Prevent stack overflow when a type references itself
addedTypes = new Set()) => {
  const addDependentSubTypes = subTypes => addDependentTypes(metadataTysMap, keepTypes, subTypes, addedTypes);
  for (const typeId of types) {
    const type = metadataTysMap.get(typeId);
    if (!type) {
      log.warn(`Unable to find type with id ${typeId}`);
      continue;
    }
    if (addedTypes.has(type.id)) continue;
    keepTypes.add(type.id);
    addedTypes.add(type.id);
    const paramTypes = type.params.map(param => param.type).filter(type => typeof type === "number");
    addDependentSubTypes(paramTypes);
    switch (type.def.tag) {
      case "array":
        addDependentSubTypes([type.def.value.type]);
        break;
      case "bitSequence":
        addDependentSubTypes([type.def.value.bitOrderType, type.def.value.bitStoreType]);
        break;
      case "compact":
        addDependentSubTypes([type.def.value]);
        break;
      case "composite":
        addDependentSubTypes(type.def.value.map(field => field.type).filter(type => typeof type === "number"));
        break;
      case "primitive":
        break;
      case "sequence":
        addDependentSubTypes([type.def.value]);
        break;
      case "tuple":
        addDependentSubTypes(type.def.value.filter(type => typeof type === "number"));
        break;
      case "variant":
        addDependentSubTypes(type.def.value.flatMap(member => member.fields.map(field => field.type)).filter(type => typeof type === "number"));
        break;
      default:
        {
          // force compilation error if any types don't have a case
          const exhaustiveCheck = type.def;
          log.error(`Unhandled V15Type type ${exhaustiveCheck}`);
        }
    }
  }
};
const remapTypeIds = (metadata, getNewTypeId) => {
  remapLookupTypeIds(metadata, getNewTypeId);
  remapStorageTypeIds(metadata, getNewTypeId);
};
const remapLookupTypeIds = (metadata, getNewTypeId) => {
  for (const type of metadata.lookup) {
    type.id = getNewTypeId(type.id);
    for (const param of type.params) {
      if (typeof param.type !== "number") continue;
      param.type = getNewTypeId(param.type);
    }
    switch (type.def.tag) {
      case "array":
        type.def.value.type = getNewTypeId(type.def.value.type);
        break;
      case "bitSequence":
        type.def.value.bitOrderType = getNewTypeId(type.def.value.bitOrderType);
        type.def.value.bitStoreType = getNewTypeId(type.def.value.bitStoreType);
        break;
      case "compact":
        type.def.value = getNewTypeId(type.def.value);
        break;
      case "composite":
        for (const field of type.def.value) {
          if (typeof field.type !== "number") continue;
          field.type = getNewTypeId(field.type);
        }
        break;
      case "primitive":
        break;
      case "sequence":
        type.def.value = getNewTypeId(type.def.value);
        break;
      case "tuple":
        type.def.value = type.def.value.map(type => {
          if (typeof type !== "number") return type;
          return getNewTypeId(type);
        });
        break;
      case "variant":
        for (const member of type.def.value) {
          for (const field of member.fields) {
            if (typeof field.type !== "number") continue;
            field.type = getNewTypeId(field.type);
          }
        }
        break;
      default:
        {
          // force compilation error if any types don't have a case
          const exhaustiveCheck = type.def;
          log.error(`Unhandled V15Type type ${exhaustiveCheck}`);
        }
    }
  }
};
const remapStorageTypeIds = (metadata, getNewTypeId) => {
  for (const pallet of metadata.pallets) {
    for (const item of pallet.storage?.items ?? []) {
      if (item.type.tag === "plain") item.type.value = getNewTypeId(item.type.value);
      if (item.type.tag === "map") {
        item.type.value.key = getNewTypeId(item.type.value.key);
        item.type.value.value = getNewTypeId(item.type.value.value);
      }
    }
  }
};

/**
 * Extracts the `version` u8 from a SCALE-encoded metadata blob and returns it as a `number`.
 *
 * Only reads the first 40 bytes of the blob.
 */
const getMetadataVersion = metadataRpc => {
  try {
    return Struct({
      magicNumber: u32,
      version: u8
    }).dec(metadataRpc).version;
  } catch {
    return 0;
  }
};

const decodeMetadata = metadataRpc => {
  const metadataVersion = getMetadataVersion(metadataRpc);
  if (metadataVersion !== 15 && metadataVersion !== 14) return {
    metadataVersion
  };
  const decoded = metadata.dec(metadataRpc);
  if (decoded.metadata.tag === "v15") return {
    metadataVersion,
    metadata: decoded.metadata.value,
    tag: decoded.metadata.tag
  };
  if (decoded.metadata.tag === "v14") return {
    metadataVersion,
    metadata: decoded.metadata.value,
    tag: decoded.metadata.tag
  };
  return {
    metadataVersion
  };
};

const decodeScale = (scaleCoder, change, error) => {
  if (change === null) return null;
  try {
    return scaleCoder?.dec(change) ?? null;
  } catch (cause) {
    log.warn(error ?? `Failed to decode ${change}`, cause);
    return null;
  }
};

const encodeMetadata = ({
  metadata: metadata$1,
  tag
}) => toHex(metadata.enc({
  magicNumber,
  metadata: tag === "v15" ? {
    tag,
    value: metadata$1
  } : {
    tag,
    value: metadata$1
  }
}));

const encodeStateKey = (scaleCoder, error, ...args) => {
  try {
    return scaleCoder?.enc(...args);
  } catch (cause) {
    log.warn(error ?? `Failed to encode stateKey ${JSON.stringify(args)}`, cause);
    return;
  }
};

/**
 * For the substrate-tokens (and other) modules, we configure the `onChainId` field in chaindata to tell the module how to query each token.
 * These queries are made to the tokens pallet.
 * E.g. api.query.Tokens.Account(accountAddress, papiParse(onChainId))
 *
 * The `onChainId` field on chaindata must be a JSON-parseable string, but for some SCALE types (especially the Binary type) we must
 * use specific `polkadot-api` classes to handle SCALE-encoding the statekey.
 *
 * Some examples:
 * Input: `5`
 * Output: `5`
 *
 * Input: `{ type: "DexShare", value: [ { type: "Token", value: { type: "ACA" } }, { type: "Token", value: { type: "AUSD" } } ] }`
 * Output: `Enum("DexShare", [Enum("Token", Enum("ACA")), Enum("Token", Enum("AUSD"))])`
 *
 * Input: `{ type: "LiquidCrowdloan", value: 13 }`
 * Output: `Enum("LiquidCrowdloan", 13)`
 *
 * Input: `{ type: "NativeToken", value: "bigint:2" }`
 * Output: `Enum("NativeToken", 2n)`
 *
 * Input: `{ type: "Erc20", value: "hex:0x07df96d1341a7d16ba1ad431e2c847d978bc2bce" }`
 * Output: `Enum("Erc20", Binary.fromHex("0x07df96d1341a7d16ba1ad431e2c847d978bc2bce"))`
 *
 * Input: `{ type: "Stellar", value: { code: "bin:TZS", issuer: "hex:0x34c94b2a4ba9e8b57b22547dcbb30f443c4cb02da3829a89aa1bd4780e4466ba" } }`
 * Output: `Enum("Stellar", { code: Binary.fromText("TZS"), issuer: Binary.fromHex("0x34c94b2a4ba9e8b57b22547dcbb30f443c4cb02da3829a89aa1bd4780e4466ba") })`
 */
const papiParse = text => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reviver = (_key, value) => {
    if (typeof value !== "string") return value;
    if (value.startsWith("bigint:")) return BigInt(value.slice("bigint:".length));
    if (value.startsWith("hex:")) return Binary.fromHex(value.slice("hex:".length));
    if (value.startsWith("bin:")) return Binary.fromText(value.slice("bin:".length));
    return value;
  };
  if (typeof text !== "string") return text;
  return JSON.parse(text, reviver);
};
const papiStringify = (value, space) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const replacer = (_key, value) => {
    if (typeof value === "bigint") return `bigint:${String(value)}`;
    if (value instanceof Binary) return `hex:${value.asHex()}`;
    return value;
  };
  return JSON.stringify(value, replacer, space);
};

export { compactMetadata, decodeMetadata, decodeScale, encodeMetadata, encodeStateKey, getMetadataVersion, magicNumber, papiParse, papiStringify };
